import React, { useMemo } from 'react';
import {
    Button,
    IconButton,
    Tooltip,
} from '@mui/material';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';
import CancelIcon from '@mui/icons-material/Cancel';
import AssetService from '../../../Services/assets.service';
import { toast } from 'react-toastify';

/** 
 * Utility for dd/mm/yyyy
 * Returns empty string if there's no valid date
 */
function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
}

/** Dark backdrop behind the modal */
const StyledBackdrop = styled('div')({
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // dark overlay
    zIndex: 1299, // slightly below the container
});

const StyledContainer = styled('div')(({ isRtl }) => ({
    position: 'fixed', // for modal
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    minHeight: '30vh',
    maxHeight: '85vh',
    overflowY: 'auto',
    minWidth: '450px',
    zIndex: 1300, // on top of backdrop
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.4)',
    border: '1px red',
    direction: isRtl ? 'rtl' : 'ltr',
}));

const StyledFormRow = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    gap: '20px',
    marginBottom: '16px',
});

const ItemColumn = styled('div')({
    flex: 1,
});

const HorizontalPair = styled('div')(({ isRtl }) => ({
    display: 'flex',
    flexDirection: isRtl ? 'row-reverse' : 'row',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
}));

const Title = styled('div')({
    fontWeight: 'bold',
});

const Value = styled('div')({});

/** Assets usually have assetGroupId only; nested assetGroup is optional. */
const getAssetGroupDisplayLabel = (asset, assetGroups = []) => {
    if (!asset) return '';

    if (asset.assetGroup?.assetGroupId != null || asset.assetGroup?.name) {
        const g = asset.assetGroup;
        return [g.assetGroupId, g.name].filter((v) => v != null && v !== '').join(' - ');
    }

    const groupId = asset.assetGroupId ?? asset.AssetGroupId;
    if (groupId == null || groupId === '') return '';

    const group = (assetGroups || []).find(
        (g) => String(g?.assetGroupId) === String(groupId)
    );
    if (group) {
        return `${group.assetGroupId} - ${group.name}`;
    }
    return String(groupId);
};

const AssetFoundDialog = ({
    open,
    asset,
    onClose,
    caseId,
    token,
    transactionId,
    fetchEnums,
    assetGroups = [],
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.language === 'he' || i18n.language === 'ar' ? 'rtl' : 'ltr';
    const isRtl = direction === 'rtl';

    const assetGroupLabel = useMemo(
        () => getAssetGroupDisplayLabel(asset, assetGroups),
        [asset, assetGroups]
    );

    if (!open) return null;

    // Remove asset from server
    const handleRemoveAsset = async () => {
        try {
            const response = await AssetService.DeleteAsset(caseId, asset.id, token);
            if (response?.success) {
                toast.success(t('AssetDeletedSuccessfully'));
                await fetchEnums?.();
                onClose();
                return;
            }
            const message =
                response?.error ||
                (typeof response === 'string' ? response : null) ||
                t('Failed to delete asset');
            toast.error(message);
        } catch (error) {
            console.error('Error removing asset:', error);
            let message = t('An error occurred while deleting asset');
            try {
                const parsed = JSON.parse(error?.message || '');
                message = parsed?.error || parsed?.message || message;
            } catch {
                if (error?.message) message = error.message;
            }
            toast.error(message);
        }
    };

    // Detach document from asset
    const handleDetach = async () => {
        try {
          await AssetService.DetachDocumentFromAsset(caseId, asset.id, transactionId, token);
          await fetchEnums();
          onClose();
        } catch (error) {
          console.error('Error detaching document:', error);
        }
      };

    return (
        <>
            {/* Dark overlay backdrop */}
            <StyledBackdrop  style={{zIndex: '2000'}}/>

            {/* Actual modal container */}
            <StyledContainer isRtl={isRtl} style={{zIndex: '2000'}}>
                {/* Header */}
                <div
                    style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        zIndex: '5000'
                    }}
                >
                    {/* Close / Exit Icon */}
                    <Tooltip title={t('Exit')}>
                        <IconButton onClick={onClose}>
                            <CancelIcon style={{ color: '#304FFF' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Warning emoji + AssetFound */}
                    <div className="edit-title-2">⚠️ {t('AssetFound')}</div>

                    <div />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <strong>
                        {t('AssetExistsForThisTransaction')}
                    </strong>
                </div>

                {/* Two-column rows with label:value (RTL possible) */}
                <StyledFormRow>
                    <ItemColumn>
                        <HorizontalPair isRtl={isRtl}>
                            <Title>{t('AssetName')}</Title>:
                            <Value>{asset?.name || ''}</Value>
                        </HorizontalPair>
                    </ItemColumn>
                    <ItemColumn>
                        <HorizontalPair isRtl={isRtl}>
                            <Title>{t('OriginalPrice')}</Title>:
                            <Value>{asset?.originalPrice ?? ''}</Value>
                        </HorizontalPair>
                    </ItemColumn>
                </StyledFormRow>

                <StyledFormRow>
                    <ItemColumn>
                        <HorizontalPair isRtl={isRtl}>
                            <Title>{t('DepreciationPercentage')}</Title>:
                            <Value>{asset?.depreciationPercentage ?? ''}</Value>
                        </HorizontalPair>
                    </ItemColumn>
                    <ItemColumn>
                        <HorizontalPair isRtl={isRtl}>
                            <Title>{t('PurchaseDate')}</Title>:
                            <Value>{formatDate(asset?.purchaseDate)}</Value>
                        </HorizontalPair>
                    </ItemColumn>
                </StyledFormRow>

                <StyledFormRow>
                    <ItemColumn>
                        <HorizontalPair isRtl={isRtl}>
                            <Title>{t('Description')}</Title>:
                            <Value>{asset?.description ?? ''}</Value>
                        </HorizontalPair>
                    </ItemColumn>
                    <ItemColumn>
                        <HorizontalPair isRtl={isRtl}>
                            <Title>{t('SellDate')}</Title>:
                            <Value>{formatDate(asset?.sellDate)}</Value>
                        </HorizontalPair>
                    </ItemColumn>
                </StyledFormRow>

                <StyledFormRow>
                    <ItemColumn>
                        <HorizontalPair isRtl={isRtl}>
                            <Title>{t('AssetGroup')}</Title>:
                            <Value>{assetGroupLabel}</Value>
                        </HorizontalPair>
                    </ItemColumn>
                    <ItemColumn>
                        <HorizontalPair isRtl={isRtl}>
                            <Title>{t('ActivationDate')}</Title>:
                            <Value>{formatDate(asset?.activationDate)}</Value>
                        </HorizontalPair>
                    </ItemColumn>
                </StyledFormRow>

                <StyledFormRow>
                    <ItemColumn />
                    <ItemColumn>
                        <HorizontalPair isRtl={isRtl}>
                            <Title>{t('SerialNumber')}</Title>:
                            <Value>{asset?.assetNumber ?? ''}</Value>
                        </HorizontalPair>
                    </ItemColumn>
                </StyledFormRow>

                {/* Action Buttons (centered) */}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px', gap: '12px' }}>
                    <Button
                        variant="outlined"
                        style={{
                            backgroundColor: 'red',
                            color: '#FFF',
                            borderRadius: '8px',
                            textTransform: 'none',
                        }}
                        onClick={handleRemoveAsset}
                    >
                        {t('RemoveAsset')}
                    </Button>
                    <Button
                        variant="contained"
                        style={{
                            backgroundColor: 'red',
                            color: '#FFF',
                            borderRadius: '8px',
                            textTransform: 'none',
                        }}
                        onClick={handleDetach}
                    >
                        {t('DetachDocument')}
                    </Button>
                </div>
            </StyledContainer>
        </>
    );
};

export default AssetFoundDialog;
